import React, {Component} from 'react';
import {
    Paper,
    Typography,
    Box,
    Grid,
    TextField,
    Skeleton,
    Divider,
    Chip,
    Tooltip,
    InputAdornment,
} from '@mui/material';
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Badge as BadgeIcon,
    ContactPhone as ContactPhoneIcon,
    Verified as VerifiedIcon,
    AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import CryptoJS from "crypto-js";
import {KEY} from "../../../dummyData";

class PersonalInfoTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            profileData: {
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                secondaryPhone: "",
                nationalId: "",
                emergencyContact: "",
                emergencyName: "",
                verifiedID: false
            }
        };
    }

    componentDidMount() {
        this.fetchUserData();
    }

    fetchUserData = () => {
        try {
            // Get data from localStorage
            let details = CryptoJS.AES.decrypt(
                localStorage.getItem("tenant_details"),
                KEY
            );

            let originalText = details.toString(CryptoJS.enc.Utf8);

            // Parse tenant details
            let tenantDetails = new Map(Object.entries(JSON.parse(originalText)));

            // Create emergency contact name
            const emergencyName = tenantDetails.get("next_of_kin_first_name") && tenantDetails.get("next_of_kin_last_name")
                ? `${tenantDetails.get("next_of_kin_first_name")} ${tenantDetails.get("next_of_kin_last_name")}`
                : "";

            // Set profile data with the correct tenant details
            this.setState({
                loading: false,
                profileData: {
                    firstName: tenantDetails.get("first_name") || "",
                    lastName: tenantDetails.get("last_name") || "",
                    email: tenantDetails.get("email_address") || "",
                    phone: tenantDetails.get("phone_number") || "",
                    secondaryPhone: tenantDetails.get("secondary_phone_number") || "",
                    nationalId: tenantDetails.get("national_id") || "",
                    emergencyContact: tenantDetails.get("next_of_kin_contacts") || "",
                    emergencyName: emergencyName,
                    verifiedID: tenantDetails.get("id_verification_status") !== "pending" || false,
                }
            });
        } catch (error) {
            console.error("Error loading profile data:", error);
            this.setState({loading: false});
        }
    };

    // Format phone number for display
    formatPhoneNumber = (phoneNumberString) => {
        const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return '(' + match[1] + ') ' + match[2] + '-' + match[3];
        }
        return phoneNumberString;
    }

    render() {
        const {loading, profileData} = this.state;
        const {className} = this.props;

        return (
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden'
                }}
                className={`personal-info-tab ${className || ''}`}
            >
                {/* Form Section */}
                <Box sx={{p: 3}} className="personal-info-form">
                    {/* Basic Information Section */}
                    <Box sx={{mb: 4}} className="basic-info-section">
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: '#1F2937',
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                            className="section-title"
                        >
                            <PersonIcon fontSize="small" sx={{color: '#3B82F6'}}/>
                            Basic Information
                        </Typography>

                        <Grid container spacing={3}>
                            {/* First Name */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="First Name"
                                        name="firstName"
                                        value={profileData.firstName}
                                        disabled
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <BadgeIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="first-name-field"
                                    />
                                )}
                            </Grid>

                            {/* Last Name */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="Last Name"
                                        name="lastName"
                                        value={profileData.lastName}
                                        disabled
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <BadgeIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="last-name-field"
                                    />
                                )}
                            </Grid>

                            {/* National ID */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="National ID"
                                        name="nationalId"
                                        value={profileData.nationalId}
                                        disabled
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <BadgeIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {profileData.verifiedID ? (
                                                        <Tooltip title="Verified">
                                                            <VerifiedIcon
                                                                fontSize="small"
                                                                sx={{color: '#10B981'}}
                                                            />
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Pending Verification">
                                                            <AccessTimeIcon
                                                                fontSize="small"
                                                                sx={{color: '#F59E0B'}}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="national-id-field"
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{my: 3}}/>

                    {/* Contact Information Section */}
                    <Box sx={{mb: 4}} className="contact-info-section">
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: '#1F2937',
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                            className="section-title"
                        >
                            <PhoneIcon fontSize="small" sx={{color: '#3B82F6'}}/>
                            Contact Information
                        </Typography>

                        <Grid container spacing={3}>

                            {/* Phone */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="Phone Number"
                                        name="phone"
                                        value={this.formatPhoneNumber(profileData.phone)}
                                        disabled
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Chip
                                                        label="Primary"
                                                        size="small"
                                                        sx={{
                                                            height: 24,
                                                            fontSize: '0.75rem',
                                                            backgroundColor: '#EBF5FF',
                                                            color: '#3B82F6'
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="phone-field"
                                    />
                                )}
                            </Grid>

                            {/* Secondary Phone */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="Secondary Phone Number"
                                        name="secondaryPhone"
                                        value={this.formatPhoneNumber(profileData.secondaryPhone)}
                                        disabled
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="secondary-phone-field"
                                    />
                                )}
                            </Grid>

                            {/* Email */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        name="email"
                                        type="email"
                                        value={profileData.email}
                                        disabled
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="email-field"
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{my: 3}}/>

                    {/* Emergency Contact Section */}
                    <Box className="emergency-contact-section">
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: '#1F2937',
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                            className="section-title"
                        >
                            <ContactPhoneIcon fontSize="small" sx={{color: '#3B82F6'}}/>
                            Emergency Contact
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Emergency Contact Name */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="Emergency Contact Name"
                                        name="emergencyName"
                                        value={profileData.emergencyName}
                                        disabled
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PersonIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="emergency-name-field"
                                    />
                                )}
                            </Grid>

                            {/* Emergency Contact Phone */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="Emergency Contact Phone"
                                        name="emergencyContact"
                                        value={this.formatPhoneNumber(profileData.emergencyContact)}
                                        disabled
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="emergency-phone-field"
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Paper>
        );
    }
}

export default PersonalInfoTab;